import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { errorNormalizer } from '../../../shared/Helpers/functions';
import {
	ACTIONS,
	behaviorTreatmentModificationReducer,
	initialBehaviorTreatmentModificationState,
} from './behaviorTreatmentModificationReducer';
import BehaviorTreatmentModificationData from './behaviorTreatmentModificationData';
import { useResetCalendarData } from '../../Calendar/Helpers/hooks';
import SoapNoteButtons from '../components/soapNoteButtons';
import { usePostSubmitEditMode } from '../helpers';

import { getSoapNote, updateBehaviorTreatmentModification } from '../../../redux/State/clientSlice/soapNoteSlice';
import { setDate, setEndTime, setStartTime } from '../../../redux/State/clientSlice/sessionSlice';
import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	BehaviorTreatmentModificationProperties,
	UpdateBehaviorTreatmentModificationProperties,
} from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

const BehaviorTreatmentModification = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const { resetData } = useResetCalendarData();
	const { isPostSubmitEditMode } = usePostSubmitEditMode();

	const reportId = params.get('reportId');
	const clientId = params.get('clientId');
	const forcedOperation = params.get('forcedOperation') === 'true';

	const sessionInfo = useSelector((state) => state.session.sessionInfo);

	const [data, setData] = useReducer(behaviorTreatmentModificationReducer, initialBehaviorTreatmentModificationState);

	const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
	const isMounted = useRef(true);

	useEffect(() => {
		isMounted.current = true;

		return () => {
			isMounted.current = false;

			if (currentUpdateRequestRef.current) {
				console.info('Aborting request on unmount');
				currentUpdateRequestRef.current.abort();
			}
		};
	}, []);

	useEffect(() => {
		if (!reportId || !clientId) return;

		dispatch(getSoapNote({ reportId, clientId }))
			.then(unwrapResult)
			.then((response) => {
				const {
					barrierToTreatmentResponses,
					behaviorTreatmentModificationInterventionResponses,
					reinforcementResponses,
					location,
					date,
					startTime,
					endTime,
					clientTelehealthLocation,
					userTelehealthLocation,
					participantResponses,
				} = response as BehaviorTreatmentModificationProperties;
				const barriers = barrierToTreatmentResponses?.map((x) => x.id);
				const interventions = behaviorTreatmentModificationInterventionResponses?.map((x) => x.id);
				const reinforcements = reinforcementResponses?.map((x) => x.id);
				const participants = participantResponses?.map((x) => x.id);

				dispatch(setDate(dayjs(date).format()));
				dispatch(setStartTime(dayjs(startTime).format()));
				dispatch(setEndTime(dayjs(endTime).format()));

				setData({
					type: ACTIONS.setState,
					payload: response,
				});

				setData({
					type: ACTIONS.setIntervention,
					payload: interventions,
				});

				setData({
					type: ACTIONS.setBarriers,
					payload: barriers,
				});

				setData({
					type: ACTIONS.setReinforcement,
					payload: reinforcements,
				});

				setData({
					type: ACTIONS.setParticipants,
					payload: participants,
				});

				if (location) {
					setData({
						type: ACTIONS.setLocationId,
						payload: location.id,
					});
				}

				if (clientTelehealthLocation) {
					setData({
						type: ACTIONS.setClientTelehealthLocationId,
						payload: clientTelehealthLocation.id,
					});
				}

				if (userTelehealthLocation) {
					setData({
						type: ACTIONS.setUserTelehealthLocationId,
						payload: userTelehealthLocation.id,
					});
				}
			})
			.catch((error) => {
				errorNormalizer(error);
				if (error.status === 403) navigate('/dashboard');
			});
	}, [dispatch, clientId, reportId, navigate]);

	const updateSoapNoteHandler = useCallback(
		(submit: boolean) => {
			if (currentUpdateRequestRef.current) {
				console.info('Aborting previous pending request');
				currentUpdateRequestRef.current.abort();
			}
			if (!reportId || !clientId || !data) return;
			const { date, startTime, endTime } = sessionInfo;
			const allData: UpdateBehaviorTreatmentModificationProperties = {
				...data,
				date,
				startTime,
				endTime,
				reportId,
				submit,
				forcedOperation,
				clientId,
			};

			const requestPromise = dispatch(updateBehaviorTreatmentModification(allData));
			currentUpdateRequestRef.current = requestPromise;

			requestPromise
				.then(unwrapResult)
				.then(() => {
					if (!submit || !isMounted.current) return;
					resetData();
					toast('Success');
					navigate('/calendar');
				})
				.catch(errorNormalizer)
				.finally(() => {
					if (currentUpdateRequestRef.current === requestPromise) {
						currentUpdateRequestRef.current = null;
					}
				});
		},
		[reportId, clientId, forcedOperation, data, sessionInfo, navigate, resetData, dispatch],
	);

	const saveHandler = useCallback(() => {
		if (isPostSubmitEditMode) return;
		updateSoapNoteHandler(false);
	}, [updateSoapNoteHandler, isPostSubmitEditMode]);
	const submitHandler = useCallback(() => updateSoapNoteHandler(true), [updateSoapNoteHandler]);

	return (
		<>
			<BehaviorTreatmentModificationData setData={setData} data={data} save={saveHandler} />

			<SoapNoteButtons save={saveHandler} submit={submitHandler} />
		</>
	);
};

export default BehaviorTreatmentModification;
